import React from 'react'
import { useSelector } from 'react-redux'
import userSelectors from '../../../../store/selectors/user.selectors'
import assignmentSelectors from '../../../../store/selectors/assignment.selectors'
import {
    AssignmentPermissions,
    PERMISSION_LEVEL_TO_PERMISSION_SCOPES,
    PermissionLevels,
    Forbiddances,
} from '../../../../constants/assignment-permissions'
import versionSelectors from '../../../../store/selectors/version.selectors'
import { ASSIGNMENT_TYPES } from '../../../../constants/assignment-types'
import PropTypes from 'prop-types'
import { useFetchAssignmentsQuery } from '../../../../store/apis/assignment.api'
import { fetchAssignmentsSelectors } from '../../../../store/apis/utils/selectors/assignment.api.selectors'
import { checkIfAssignmentIsProcessing } from '../../../../utils/assignment.helper'
import Routes from '../../../../constants/routes'
import { useVersionId } from '../../../../utils/parseURL.helper'
import { isNil } from 'lodash'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import { useIsCurrentRoute } from '../../../../hooks/useIsCurrentRoute'
import useAssignmentFilters from '../../../../hooks/useAssignmentFilters'

const usePermissionLevel = token => {
    const permissionLevelFromStore = useSelector(assignmentSelectors.permissionLevel)
    const queryArgs = useAssignmentFilters()
    
    const { data: permissionLevelByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token,
        selectFromResult: result => fetchAssignmentsSelectors.selectPermissionLevelByToken({ token }, result),
    })

    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    if (!isAssignmentsPage) return permissionLevelFromStore

    return permissionLevelByTokenFromApi || permissionLevelFromStore
}

const useProcessingStatus = token => {
    const processingStatusFromStore = useSelector(assignmentSelectors.processingStatus)
    const queryArgs = useAssignmentFilters()
    
    const { data: processingStatusByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token,
        selectFromResult: result => fetchAssignmentsSelectors.selectProcessingStatusByToken({ token }, result),
    })

    // todo remove this once we have a proper way to get the processing status from the backend
    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    if (!isAssignmentsPage) return processingStatusFromStore
    // todo remove this once we have a proper way to get the processing status from the backend

    return processingStatusByTokenFromApi || processingStatusFromStore
}

const useType = token => {
    const typeFromStore = useSelector(assignmentSelectors.type)
    const queryArgs = useAssignmentFilters()
    
    const { data: typeByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token,
        selectFromResult: result => fetchAssignmentsSelectors.selectTypeByToken({ token }, result),
    })

    // todo remove this once we have a proper way to get the type from the backend
    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    if (!isAssignmentsPage) return typeFromStore
    // todo remove this once we have a proper way to get the type from the backend

    return typeByTokenFromApi || typeFromStore
}

const useDoesVersionHaveMbas = token => {
    const doesVersionHaveMbasFromStore = useSelector(versionSelectors.currentHasMbas)
    const queryArgs = useAssignmentFilters()
    
    const { data: doesVersionHaveMbasByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token,
        selectFromResult: result => fetchAssignmentsSelectors.selectDoesVersionHaveMbasByToken({ token }, result),
    })

    // todo remove this once we have a proper way to get the does version have mbas from the backend
    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    if (!isAssignmentsPage) return doesVersionHaveMbasFromStore
    // todo remove this once we have a proper way to get the does version have mbas from the backend

    return doesVersionHaveMbasByTokenFromApi ?? doesVersionHaveMbasFromStore
}

const useIsVersionLatest = () => {
    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    let versionId = useVersionId()
    versionId = versionId ? parseInt(versionId) : null
    const isVersionLatest = useSelector(versionSelectors.isLatestById(versionId))
    return isAssignmentsPage ? false : isVersionLatest // on assignments and shared assignments pages we can't know if the version is latest, we see the default one
}

const useIsVersionPublished = token => {
    let versionId = useVersionId()
    versionId = versionId ? parseInt(versionId) : null

    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    const isVersionPublishedFromStore = useSelector(versionSelectors.isPublishedById(versionId))
    const queryArgs = useAssignmentFilters()
    
    const { data: isVersionPublishedByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token || !isAssignmentsPage,
        selectFromResult: result => fetchAssignmentsSelectors.selectIsVersionPublishedByToken({ token }, result),
    })

    // todo remove this once we have a proper way to get the is version published from the backend
    if (!isAssignmentsPage) return isVersionPublishedFromStore
    // todo remove this once we have a proper way to get the is version published from the backend

    return isVersionPublishedByTokenFromApi ?? isVersionPublishedFromStore
}

export const useAssignmentAccess = (
    permissions = [],
    skipForbiddances = [],
    token,
    assigneeId,
) => {
    const userId = useSelector(userSelectors.id)
    const isSenseUser = useSelector(userSelectors.isSenseUser)
    const isGatechUser = useSelector(userSelectors.isGatechUser)
    const pendingApprovalPatternId = useSelector(patternSelectors.pendingApprovalPatternId)
    const doesVersionHaveMbas = useDoesVersionHaveMbas(token)
    const isVersionLatest = useIsVersionLatest()
    const isVersionPublished = useIsVersionPublished(token)
    const type = useType(token)
    const processingStatus = useProcessingStatus(token)

    const permissionLevel = usePermissionLevel(token)
    const isOwner = permissionLevel === PermissionLevels.OWNER

    let userPermissions = permissionLevel 
        ? [...(PERMISSION_LEVEL_TO_PERMISSION_SCOPES[permissionLevel] || [])]
        : isOwner 
            ? Object.values(AssignmentPermissions) // fallback for cases where access_type is not available
            : []

    /**
     * Controls assignment-based access using a three-state system with granular permissions:
     * 
     * assigneeId === undefined:
     *   - Open access (not assignment-specific)
     *   - All users with basic permissions can access
     * 
     * assigneeId === null:
     *   - Unassigned items (awaiting assignment)
     *   - Accessible by users with CAN_EDIT_UNASSIGNED permission
     * 
     * assigneeId === number:
     *   - Assigned to specific user
     *   - Accessible by: assignee, users with CAN_EDIT_ASSIGNED_TO_OTHERS permission
     * 
     * This replaces the previous owner-only system with role-based granular permissions.
     */
    const canEditAssignedEntity = 
        assigneeId === undefined || 
        assigneeId === userId || 
        (assigneeId === null && userPermissions.includes(AssignmentPermissions.CAN_EDIT_UNASSIGNED)) ||
        (typeof assigneeId === 'number' && userPermissions.includes(AssignmentPermissions.CAN_EDIT_ASSIGNED_TO_OTHERS))

    if (isSenseUser || isGatechUser) userPermissions.push(AssignmentPermissions.CAN_USE_GPT)
    const hasPermissions = permissions.every(requiredPermission => userPermissions.includes(requiredPermission))

    const generalForbiddancePrerequisitesAreMet =
        (skipForbiddances.includes(Forbiddances.IS_IN_CONFIRM_CHANGES_MODE)
            ? true
            : isNil(pendingApprovalPatternId)) &&
        (skipForbiddances.includes(Forbiddances.VERSION_HAS_MBAS)
            ? true
            : !doesVersionHaveMbas) &&
        (skipForbiddances.includes(Forbiddances.IS_VERSION_NOT_LATEST)
            ? true
            : isVersionLatest) &&
        (skipForbiddances.includes(Forbiddances.IS_VERSION_PUBLISHED)
            ? true
            : !isVersionPublished) &&
        (skipForbiddances.includes(Forbiddances.IS_PROCESSING)
            ? true
            : !checkIfAssignmentIsProcessing(processingStatus)) &&
        (skipForbiddances.includes(Forbiddances.IS_MBA)
            ? true
            : type !== ASSIGNMENT_TYPES.MODEL_BOUND_ASSIGNMENT.value)

    return hasPermissions && generalForbiddancePrerequisitesAreMet && canEditAssignedEntity
}

/**
 * TODO (Nazar, 2025-05-28):
 * ──────────────────────────────────────────────────────────────────────────────
 * Current API:
 *   <Access permissions={[...]} skipForbiddances={[...]}>
 *
 * Planned refactor:
 *   • Collapse the two props into a single, *positive* rules list:
 *       <Access rules={[Perm.CAN_EDIT, State.IS_UNPUBLISHED, …]} />
 *   • Every item in `rules` must evaluate to TRUE for access to be granted.
 *   • `Perm`  → actionable abilities (CAN_EDIT, CAN_DELETE, …)
 *     `State` → factual conditions (IS_UNPUBLISHED, IS_LATEST, IS_STANDARD_ASSIGNMENT)
 *
 * Benefits:
 *   - Zero double-negative thinking (no “skip”, no “NOT_” prefixes).
 *   - Declarative & readable: the JSX shows exactly what must hold.
 *   - Extensible: feature flags or A/B checks slot in as new `State` values.
 *
 * Implementation notes:
 *   1. Define two enums (Perm, State) with *positive* names only.
 *   2. `useAssignmentAccess(rules)` will:
 *        - check permissions for every `Perm`
 *        - verify each `State` via the existing selectors/hook logic
 *   3. Gate succeeds only if `rules.every(...)` returns true **and**
 *      the entity is editable by the user.
 *
 * This comment stays until the refactor lands, then both `permissions`
 * and `skipForbiddances` props will be removed.
 * ──────────────────────────────────────────────────────────────────────────────
 */
const Access = ({
    children,
    fallback = null,
    permissions = [],
    skipForbiddances = [],
    token = null,
    assigneeId = null,
    ...rest
}) => {
    const hasPermissions = useAssignmentAccess(
        permissions,
        skipForbiddances,
        token,
        assigneeId,
    )
    return hasPermissions
        ? React.Children.map(children, child => {
              if (React.isValidElement(child)) {
                  const oldOnChange = child.props.onChange
                  const newOnChange = rest.onChange
                  const mergedOnChange = event => {
                      oldOnChange && oldOnChange(event)
                      newOnChange && newOnChange(event)
                  }
                  return React.cloneElement(child, { ...rest, onChange: mergedOnChange })
              }
              return child
          })
        : fallback
}

Access.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    fallback: PropTypes.element,
    permissions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AssignmentPermissions))),
    skipForbiddances: PropTypes.arrayOf(
        PropTypes.oneOf(Object.values(Forbiddances)),
    ),
    token: PropTypes.string, // if passed, the permissions will be checked based on the assignments list from useFetchAssignmentsQuery
    /**
     * Controls assignment-based access using a three-state system with granular permissions:
     * 
     * assigneeId === undefined:
     *   - Open access (not assignment-specific)
     *   - All users with basic permissions can access
     * 
     * assigneeId === null:
     *   - Unassigned items (awaiting assignment)
     *   - Accessible by users with CAN_EDIT_UNASSIGNED permission
     * 
     * assigneeId === number:
     *   - Assigned to specific user
     *   - Accessible by: assignee, users with CAN_EDIT_ASSIGNED_TO_OTHERS permission
     * 
     * This replaces the previous owner-only system with role-based granular permissions.
     */
    assigneeId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null, undefined])]),
}

export default Access
